import { useState, useEffect, useRef } from 'react';

/**
 * Detects when an element enters the viewport
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - Visibility threshold (0-1, default 0.1)
 * @param {string} options.rootMargin - Margin around root (default '0px')
 * @param {boolean} options.triggerOnce - Only trigger once (default true)
 * @returns {[React.RefObject, boolean]} Ref to attach and visibility state
 */
export function useIntersectionObserver(options = {}) {
  const { 
    threshold = 0.1, 
    rootMargin = '0px',
    triggerOnce = true 
  } = options;
  
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Skip if already triggered and triggerOnce is true
    if (triggerOnce && hasTriggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasTriggered(true);
          
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce, hasTriggered]);

  return [ref, isVisible];
}

export default useIntersectionObserver;
